import NP from 'number-precision'

export const ALL_DOCUMENT_YEARS = 'All Years'

const DOCUMENT_PREFERENCE_FIELDS = {
    1: {
        filterYear: 'estimateFilterYear',
        groupBy: 'estimateGroupBy',
        sortType: 'estimateSortType',
        sortAscending: 'estimateSortAscending'
    },
    2: {
        filterYear: 'purchaseOrderFilterYear',
        groupBy: 'purchaseOrderGroupBy',
        sortType: 'purchaseOrderSortType',
        sortAscending: 'purchaseOrderSortAscending'
    },
    3: {
        filterYear: 'creditMemoFilterYear',
        groupBy: 'creditMemoGroupBy',
        sortType: 'creditMemoSortType',
        sortAscending: 'creditMemoSortAscending'
    }
}

export function readDocumentPreferences(setting, documentType) {
    const fields = requirePreferenceFields(documentType)
    return normalizeDocumentPreferences({
        filterYear: setting == null ? null : setting[fields.filterYear],
        groupBy: setting == null ? null : setting[fields.groupBy],
        sortType: setting == null ? null : setting[fields.sortType],
        sortAscending: setting == null ? null : setting[fields.sortAscending]
    })
}

export function buildDocumentPreferenceParams(preferences, documentType) {
    preferences = normalizeDocumentPreferences(preferences)
    const fields = requirePreferenceFields(documentType)
    return {
        [fields.filterYear]: preferences.filterYear,
        [fields.groupBy]: preferences.groupBy,
        [fields.sortType]: preferences.sortType,
        [fields.sortAscending]: preferences.sortAscending
    }
}

export function validateDocumentPreferences(preferences) {
    return normalizeDocumentPreferences(preferences)
}

function normalizeDocumentPreferences(preferences) {
    const source = preferences || {}
    const groupBy = Number(source.groupBy)
    const sortType = Number(source.sortType)
    const sortAscending = Number(source.sortAscending)
    const year = Number(source.filterYear)
    return {
        filterYear: source.filterYear === ALL_DOCUMENT_YEARS
            || (Number.isInteger(year) && year >= 1970 && year <= 3000)
            ? (source.filterYear === ALL_DOCUMENT_YEARS ? ALL_DOCUMENT_YEARS : String(year))
            : ALL_DOCUMENT_YEARS,
        groupBy: [0, 1, 2].includes(groupBy) ? groupBy : 2,
        sortType: [0, 1, 4].includes(sortType) ? sortType : 0,
        sortAscending: [0, 1].includes(sortAscending) ? sortAscending : 0
    }
}

export function documentSortColumn(sortType) {
    const normalizedSortType = Number(sortType)
    if (normalizedSortType === 1) return 'numPrefix,num'
    if (normalizedSortType === 4) return 'userClientName'
    return 'invoiceStartDate'
}

export function buildDocumentSections(documents, groupBy, orderName, ascending, locale) {
    const groups = new Map()
    documents.forEach(document => {
        const group = documentGroup(document, groupBy, locale)
        if (!groups.has(group.key)) {
            groups.set(group.key, {
                key: group.key,
                title: group.title,
                sortValue: group.sortValue,
                documents: []
            })
        }
        groups.get(group.key).documents.push(document)
    })

    const sections = Array.from(groups.values())
    sections.sort((left, right) => ascending
        ? left.sortValue - right.sortValue
        : right.sortValue - left.sortValue)
    sections.forEach(section => {
        section.documents.sort((left, right) => compareDocument(left, right, orderName, ascending, locale))
        section.total = section.documents.reduce((sum, document) => NP.plus(sum, Number(document.totalMoney)), 0)
    })
    return sections
}

function requirePreferenceFields(documentType) {
    const fields = DOCUMENT_PREFERENCE_FIELDS[documentType]
    if (fields == null) {
        throw new Error('Document type is invalid')
    }
    return fields
}

function documentDateEpoch(document) {
    return Number(document.invoiceStartDate == null ? 0 : document.invoiceStartDate)
}

function documentGroup(document, groupBy, locale) {
    const date = new Date(documentDateEpoch(document) * 1000)
    const year = date.getFullYear()
    if (groupBy === 0) {
        return {key: `year:${year}`, title: String(year), sortValue: year}
    }
    if (groupBy === 1) {
        const quarter = Math.floor(date.getMonth() / 3) + 1
        return {key: `quarter:${year}:${quarter}`, title: `Q${quarter} ${year}`, sortValue: year * 10 + quarter}
    }
    if (groupBy === 2) {
        const month = date.getMonth()
        const title = new Intl.DateTimeFormat(locale, {year: 'numeric', month: 'long'}).format(date)
        return {key: `month:${year}:${month}`, title, sortValue: year * 100 + month}
    }
    return {key: `month:${year}:${date.getMonth()}`, title: new Intl.DateTimeFormat(locale, {
        year: 'numeric', month: 'long'
    }).format(date), sortValue: year * 100 + date.getMonth()}
}

function compareDocument(left, right, orderName, ascending, locale) {
    let comparison
    if (orderName === 'invoiceStartDate') {
        comparison = documentDateEpoch(left) - documentDateEpoch(right)
    } else if (orderName === 'numPrefix,num') {
        comparison = compareDocumentNumber(left, right, locale)
    } else if (orderName === 'userClientName') {
        comparison = String(left.userClientName || '').localeCompare(
            String(right.userClientName || ''), locale, {sensitivity: 'base'}
        )
    } else if (orderName === 'totalMoney') {
        comparison = Number(left.totalMoney) - Number(right.totalMoney)
    } else {
        comparison = documentDateEpoch(left) - documentDateEpoch(right)
    }
    if (comparison === 0) {
        comparison = documentDateEpoch(left) - documentDateEpoch(right)
    }
    if (comparison === 0) {
        comparison = String(left.innerUUID).localeCompare(String(right.innerUUID))
    }
    return ascending ? comparison : -comparison
}

function compareDocumentNumber(left, right, locale) {
    const prefixComparison = String(left.numPrefix || '').localeCompare(
        String(right.numPrefix || ''), locale, {numeric: true, sensitivity: 'base'}
    )
    if (prefixComparison !== 0) {
        return prefixComparison
    }
    return String(left.num || '').localeCompare(String(right.num || ''), locale, {
        numeric: true,
        sensitivity: 'base'
    })
}
